import { Box, Button, Collapse, Typography } from "@outschool/backpack";
import { TextMimeType } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  AttachmentGallery,
  TextWithLinks,
} from "@outschool/ui-components-shared";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import { RichTextView } from "@outschool/ui-rich-text-editor";
import React, { FC, useState } from "react";

import { POST_CONTENT_TRUNCATE_LENGTH } from "../../../lib/constants";
import { MessageContent, Sender } from "../types";

export interface ClassPostContentProps {
  sender: Sender;
  messageContent: MessageContent;
  trackingLabel?: string;
  trackingMetadata?: TrackingProperties;
  allowRichText?: boolean;
  /**
   * Height of the collapsed content in pixels.
   * @default 150
   */
  collapseSize?: number;
}

const ClassPostContent: FC<ClassPostContentProps> = ({
  sender,
  messageContent,
  trackingLabel,
  trackingMetadata,
  allowRichText = false,
  collapseSize = 150,
}) => {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\ClassPost\\ClassPostContent",
  );
  const [userExpanded, setUserExpanded] = useState(false);

  // Put ref in state because we need it to cause a re-render so that isLongContent recalculates
  const [contentRef, setContentRef] = React.useState<
    HTMLDivElement | HTMLPreElement | null
  >(null);
  const contentSize = contentRef?.scrollHeight;

  // Short content by character count is displayed expanded immediately.
  // Long content uses measured height to determine if collapse is needed.
  const textLength = messageContent.text?.length ?? 0;
  const htmlTextLength = messageContent.htmlText?.length ?? 0;
  const contentLength = Math.max(textLength, htmlTextLength);
  const isLongContent =
    contentLength >= POST_CONTENT_TRUNCATE_LENGTH &&
    (!contentSize || contentSize > collapseSize);
  const expanded = userExpanded || !isLongContent;
  const showExpandButton = !expanded;

  return (
    <Box data-test-id="ClassPostContent">
      <Box
        sx={{
          marginBottom: 40,
          position: "relative",
          paddingY: 16,
        }}
      >
        <Collapse
          in={expanded}
          collapsedSize={
            // Don't require collapsed content to be bigger than it needs to be
            contentSize && contentSize < collapseSize
              ? contentSize
              : collapseSize
          }
        >
          {allowRichText &&
          messageContent.originalMimeType === TextMimeType.TextMarkdown ? (
            <RichTextView
              content={messageContent.htmlText}
              allowLinks={true}
              maxLines={Infinity}
              allowEmails={false}
              ref={setContentRef}
            />
          ) : (
            <Box
              component="pre"
              sx={{ wordBreak: "break-word", margin: 0 }}
              ref={setContentRef}
            >
              <TextWithLinks
                clickableLinks={true}
                text={messageContent.text?.trim() ?? ""}
                maxTextLength={Infinity}
                hideEmails={true}
              />
            </Box>
          )}
        </Collapse>
        {showExpandButton && (
          <Box
            // Make the button float in the margin so that when it disappears it doesn't make content jump
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              position: "absolute",
              bottom: -24,
            }}
          >
            <Typography
              variant="subtitle1"
              color="textSecondary"
              sx={{ userSelect: "none" }}
            >
              ⋯
            </Typography>
            <Button
              onClick={() => setUserExpanded(true)}
              variant="link"
              size="small"
            >
              {t`Show more`}
            </Button>
          </Box>
        )}
      </Box>
      <AttachmentGallery
        video={messageContent.video}
        attachments={messageContent.attachments}
        trackingLabel={trackingLabel}
        trackingMetadata={trackingMetadata}
        senderName={sender.name}
        isPostOwner={false}
      />
    </Box>
  );
};

export default ClassPostContent;
